import { API_HOST } from './config'
import { fetchWithTimeout } from './support'
import type { ApiEnvelope } from './types'

/**
 * The meeting host's lobby and in-call end (ADR 0014). Like moderation, these go to our API with
 * the host's credential; the server re-checks that the caller hosts this meeting (403 otherwise).
 */

export interface Knock {
  id: string
  name: string
  guest: boolean
  at: string
}

export type Credential = { code?: string | null; reconnectToken?: string | null }

const REQUEST_TIMEOUT_MS = 10_000

async function call<T>(path: string, credential: Credential, extra: object = {}): Promise<T> {
  const response = await fetchWithTimeout(
    `${API_HOST}/api/v1/public/livekit/${path}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      body: JSON.stringify({
        code: credential.code ?? undefined,
        reconnect_token: credential.reconnectToken ?? undefined,
        ...extra,
      }),
    },
    REQUEST_TIMEOUT_MS,
  )
  let envelope: ApiEnvelope<T> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<T>
  } catch {
    /* classified by status below */
  }
  if (!response.ok || envelope?.status !== 'success' || !envelope) {
    throw new Error(envelope?.message ?? 'That did not work — try again')
  }
  return envelope.data
}

export async function listLobby(credential: Credential): Promise<Knock[]> {
  return (await call<{ knocks: Knock[] }>('lobby', credential)).knocks
}

export function decideLobby(credential: Credential, ids: string[], admit: boolean) {
  return call<{ changed: number }>(admit ? 'lobby_admit' : 'lobby_deny', credential, {
    knock_ids: ids,
  })
}

/** Ends the meeting for everyone. An already-ended meeting counts as done. */
export async function endMeeting(credential: Credential): Promise<void> {
  try {
    await call('end_meeting', credential)
  } catch (error) {
    if (!/ended/i.test((error as Error).message)) throw error
  }
}
